/**
 * Server-side exception noise filters — keep in sync with client `before_send`
 * abort handling in instrumentation-client.ts.
 */

import { isInvalidRefreshTokenError } from "@/shared/lib/supabase/auth-errors"

/** Web Crypto verify failure on stale session JWT (PostHog 019e548b-f4d4). */
export function isBenignStaleAuthCryptoError(message: string): boolean {
  return message.trim() === "No suitable key or wrong key type"
}

/**
 * Identifies a persisted Supabase session signed by a key that has since been
 * rotated. These errors need local cookie cleanup, not a logout API request:
 * Supabase cannot authenticate that request with the same stale JWT.
 */
export function isStaleAuthSessionError(error: unknown): boolean {
  let message: string
  if (error instanceof Error) {
    message = error.message
  } else if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    message = error.message
  } else {
    message = String(error ?? "")
  }

  const lower = message.toLowerCase()
  return (
    lower.includes("unrecognized jwt kid") ||
    (lower.includes("invalid jwt") && lower.includes("token is unverifiable")) ||
    lower.includes("no suitable key or wrong key type")
  )
}

/** Supabase HTTP responses that are expected user/auth states, not bugs. */
export function isBenignSupabaseHttpError(
  status: number,
  message: string,
  resource?: string,
  errorCode?: string,
  errorDetails?: string
): boolean {
  const lower = message.toLowerCase()
  if (status === 400) {
    const isAuthToken = resource === "auth:token" || lower.includes("auth:token")
    if (isAuthToken && lower.includes("invalid login credentials")) return true

    // Failed token refresh on an expired/rotated/missing refresh token. This is
    // the routine "session fully expired, sign in again" state — `invokeEdgeFunction`
    // calls `refreshSession()` on a 401 and this is the expected outcome when the
    // refresh token is also dead. Without this, reporting-fetch would mint a fresh
    // SupabaseHttp400 issue per expired session, relocating the very noise the
    // refresh-and-retry was meant to remove.
    if (isAuthToken && isInvalidRefreshTokenError({ message })) {
      return true
    }

    // Check-constraint rejection of an unsupported preferred_locale on the
    // profiles PATCH. `updatePreferredLocale` guards with isEnabledLocale and
    // degrades to a "Failed to save language" toast — a handled, business-
    // expected state, not a bug. Without this, reporting-fetch captures it as a
    // brand-new SupabaseHttp400 error-tracking issue.
    return lower.includes("profiles_preferred_locale_supported")
  }

  // A conversation can disappear between navigation and this optional
  // settings read. PostgREST reports that expected zero-row result as 406
  // PGRST116; the client query falls back to the default settings.
  if (
    status === 406 &&
    resource === "conversations" &&
    errorCode === "PGRST116" &&
    errorDetails === "The result contains 0 rows" &&
    lower.includes("cannot coerce the result to a single json object")
  ) {
    return true
  }

  if (status !== 403) return false
  return (
    lower.includes("unrecognized jwt kid") ||
    (lower.includes("invalid jwt") && lower.includes("token is unverifiable"))
  )
}

export function isBenignServerException(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "")
  const name = err instanceof Error ? err.name : ""

  if (name === "AbortError") return true
  if (msg === "aborted") return true
  if (msg.includes("AbortError")) return true
  if (msg.includes("signal is aborted")) return true
  if (msg.includes("operation was aborted")) return true

  if (name.startsWith("SupabaseHttp")) {
    const status = Number.parseInt(name.slice("SupabaseHttp".length), 10)
    if (!Number.isNaN(status) && isBenignSupabaseHttpError(status, msg)) return true
  }

  if (isBenignStaleAuthCryptoError(msg)) return true

  return false
}
